import useIsVisible from "../hooks/useIsVisible";
import {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

import { Box, Chip, List, ListItem, Typography, Button } from "@mui/material";
import WithDividers from "../helpers/WithDividers";
import Loading from "../common/Loading";
import { useFetchPhoneInbox } from "services/advisor";
import DateDisplay from "../common/DateDisplay";
import {
  FetchPhoneInboxResponse_InboxEntry,
  FetchPhoneInboxResponse_InboxEntryType,
  FetchPhoneInboxResponse_InboxTreatment,
} from "protogen/advisors_service_pb";
import { CurrentUserContext } from "../context/RequireAuth";
import { CurrentUser } from "protogen/auth_pb";
import StartExternalConversation from "../creation/StartExternalConversation";
import {
  Phone as PhoneIcon,
  MessageSquare as MessageSquareIcon,
} from "lucide-react";
import useIsMobile from "../hooks/useIsMobile";
import { InboxHandle } from "./utils";
import { ReactComponent as EllipseIcon } from "../../icons/Menu/Ellipse.svg";
import { useWebSocketContext } from "components/context/SocketConsumerProvider";

const toMe = (
  entry: FetchPhoneInboxResponse_InboxEntry,
  currentUser: CurrentUser,
) => entry.recipient?.ref === currentUser.ref;
const fromMe = (
  entry: FetchPhoneInboxResponse_InboxEntry,
  currentUser: CurrentUser,
) => entry.sender?.ref === currentUser.ref;
const withMe = (
  entry: FetchPhoneInboxResponse_InboxEntry,
  currentUser: CurrentUser,
) => toMe(entry, currentUser) || fromMe(entry, currentUser);

const EntryTS = ({ t }: { t: bigint }) => (
  <DateDisplay
    date={new Date(Number(t) * 1000)}
    sx={{
      color: "rgba(0, 0, 0, 0.5)",
    }}
  />
);

interface EntryItemProps {
  entry: FetchPhoneInboxResponse_InboxEntry;
  currentUser: CurrentUser;
  onClick: (entryRef: string) => void;
  selected?: boolean;
}

const callTitle = (
  entry: FetchPhoneInboxResponse_InboxEntry,
  currentUser: CurrentUser,
) => {
  if (toMe(entry, currentUser)) {
    return `Call from ${entry.sender?.displayName}`;
  } else if (fromMe(entry, currentUser)) {
    return `Call to ${entry.recipient?.displayName}`;
  } else {
    return `Call between ${entry.sender?.displayName} and ${entry.recipient?.displayName}`;
  }
};

const messageTitle = (
  entry: FetchPhoneInboxResponse_InboxEntry,
  currentUser: CurrentUser,
) => {
  if (withMe(entry, currentUser)) {
    const them =
      entry.sender?.ref === currentUser.ref ? entry.recipient : entry.sender;
    if (entry.isGroupConversation) {
      return `${them?.firstName} and others`;
    } else {
      return `${them?.displayName}`;
    }
  } else {
    return `${entry.sender?.displayName}, ${entry.recipient?.displayName}`;
  }
};

const EntryItem = ({
  currentUser,
  entry,
  onClick,
  selected = false,
}: EntryItemProps) => {
  const isMobile = useIsMobile();
  const isCall = entry.type === FetchPhoneInboxResponse_InboxEntryType.CALL;
  const title = isCall
    ? callTitle(entry, currentUser)
    : messageTitle(entry, currentUser);
  const [markedRead] = useState(false);
  const unread =
    entry.treatment === FetchPhoneInboxResponse_InboxTreatment.UNREAD &&
    !markedRead;
  const missedCall =
    entry.treatment === FetchPhoneInboxResponse_InboxTreatment.MISSED;
  return (
    <ListItem
      onClick={() => {
        onClick && onClick(entry.ref);
      }}
      key={entry.ref}
      sx={{
        cursor: "pointer",
        display: "flex",
        gap: "16px",
        width: "100%",
        padding: "16px 20px",
        justifyContent: "space-between",
        alignItems: "start",
        backgroundColor: selected ? "#FAF9FA" : "white",
        ...(missedCall
          ? {
              border: "1px solid #EAECF0",
              backgroundColor: "#F2F4F7",
            }
          : {}),
      }}
    >
      <Box sx={{ color: "#198282", marginTop: "2px" }}>
        {isCall ? (
          <PhoneIcon width="20px" />
        ) : (
          <MessageSquareIcon width="20px" />
        )}
      </Box>
      <Box display="flex" flexDirection="column" flexGrow={1} gap="4px">
        <Box
          sx={{
            display: "flex",
            width: "100%",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography
            variant="body"
            color="text.primary"
            sx={{ fontWeight: "600", lineHight: "130%" }}
          >
            {title}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <EntryTS t={entry.timestampSec} />
            <Box
              style={{
                display: "flex",
                alignItems: "start",
                textAlign: "end",
                gap: "4px",
                ...(isMobile
                  ? {
                      flexDirection: "column-reverse",
                    }
                  : {}),
              }}
            >
              {unread && <EllipseIcon height={8} width={8} />}
            </Box>
          </Box>
        </Box>
        <Typography
          variant="bodySmall"
          color={unread ? "text.primary" : "text.secondary"}
          fontWeight={unread ? "600" : "500"}
          sx={{
            margin: "0px 0px",
            wordWrap: "break-word",
            wordBreak: "anywhere",
            overflow: "hidden",
            maxHeight: "40px",
          }}
        >
          {entry.title}
        </Typography>

        {/* Family chip */}
        {entry.family && (
          <Box>
            <Chip
              label={entry.family?.name}
              color="default"
              size="small"
              sx={{ background: "#F2F2F2", marginTop: "4px" }}
            />
          </Box>
        )}
      </Box>
    </ListItem>
  );
};

interface PhoneInboxProps {
  selectedRef?: string;
  onEntrySelect(entryRef: string): void;
}

const combineEntries = (
  prev: FetchPhoneInboxResponse_InboxEntry[],
  next: FetchPhoneInboxResponse_InboxEntry[],
) => {
  const seen = new Set(prev.map((entry) => entry.ref));
  return [...prev, ...next.filter((entry) => !seen.has(entry.ref))];
};

export default forwardRef<InboxHandle, PhoneInboxProps>(
  ({ selectedRef, onEntrySelect }: PhoneInboxProps, ref) => {
    const currentUser = useContext(CurrentUserContext);
    const isMobile = useIsMobile();
    const { isVisible } = useIsVisible({});
    const [modalOpen, setModalOpen] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [entries, setEntries] = useState<
      FetchPhoneInboxResponse_InboxEntry[]
    >([]);
    const [feedCursor, setFeedCursor] = useState<string>("");
    const [hasMore, setHasMore] = useState<boolean>(false);
    const [loadingMore, setLoadingMore] = useState<boolean>(false);
    const hasLoadedOnce = useRef(false);
    const { registerHandler, unregisterHandler } = useWebSocketContext();
    const { request } = useFetchPhoneInbox();

    const refresh = async (cursor?: string) => {
      if (cursor) {
        setLoadingMore(true);
      }

      try {
        const response = await request({
          cursor: cursor || "",
        });

        if (!response) return;

        setLoaded(true);

        if (cursor || !hasLoadedOnce.current) {
          // Update cursor and hasMore state
          setFeedCursor(response.nextCursor);
          setHasMore(!!response.nextCursor);
        }

        hasLoadedOnce.current = true;

        if (cursor) {
          // Append new entries for pagination
          setEntries((pastEntries) =>
            combineEntries(pastEntries, response.entries),
          );
        } else {
          // Replace entries for refresh
          setEntries(response.entries);
        }
      } finally {
        setLoadingMore(false);
      }
    };

    const nextPage = async () => {
      if (feedCursor) {
        refresh(feedCursor);
      }
    };

    useImperativeHandle(ref, () => ({
      triggerCompose: () => {
        if (isMobile) {
          setModalOpen(true);
        }
      },
      triggerRefresh: () => refresh(),
    }));

    useEffect(() => {
      if (!hasLoadedOnce.current) {
        refresh();
      }
      const pushHandler = () => {
        if (isVisible) {
          refresh();
        }
      };
      const channels = ["textmessage/", "phonecall/"];
      registerHandler(channels, pushHandler);
      return () => {
        unregisterHandler(channels, pushHandler);
      };
    }, []);

    if (!loaded) return <Loading />;
    return (
      <Box
        sx={{
          overflowY: "auto",
          borderTop: "1px solid #E2E3E4",
          "&::-webkit-scrollbar": {
            display: "none",
          },
        }}
      >
        <List sx={{ paddingTop: 0 }}>
          <WithDividers>
            {entries.map((entry) => (
              <EntryItem
                key={entry.ref}
                currentUser={currentUser}
                entry={entry}
                onClick={() => onEntrySelect(entry.ref)}
                selected={selectedRef === entry.ref}
              />
            ))}
          </WithDividers>
          {loaded && entries.length >= 0 && hasMore && (
            <Box display="flex" justifyContent="center" alignItems="center">
              <Button
                sx={{ marginTop: "4px", marginBottom: "20px" }}
                variant="outlined"
                disabled={loadingMore || !hasMore}
                onClick={nextPage}
              >
                Load more
              </Button>
            </Box>
          )}
        </List>

        <StartExternalConversation
          open={modalOpen}
          onClose={() => setModalOpen(false)}
        />
      </Box>
    );
  },
);
